import AddIcon from '@mui/icons-material/Add'
import { Box, Button } from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid-pro'
import { useContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Page } from '../../components'
import { OutlinedBox } from '../../components/SharedUI'
import { ROLink } from '../../components/SharedUI/ROLink'
import {
  useAppointmentLookup,
  useCustomerLookup,
  useVehicleLookup
} from '../../hooks'
import { AppRoutes } from '../../models'
import { AuthContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { useGetRepairOrdersQuery } from '../../redux/api'
import { describeVehicle, formatCurrency } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import { localDateTimeArrayToDate } from '../../utils/localDateTimeArraytoDate/localDateTimeArrayToDate'
const columns: GridColDef[] = [
  {
    field: 'localId',
    headerName: 'Est #',
    description: 'Unique identifier',
    width: 100,
    renderCell: (params: GridRenderCellParams) => (
      <ROLink
        displayed={'EST #' + params.value}
        repairOrderId={params.id as string}
      />
    )
  },

  {
    field: 'customer',
    headerName: 'Customer',
    description:
      'The name of the associated customer, or the ID if the name could not be found.',
    width: 150
  },
  {
    field: 'vehicle',
    headerName: 'Vehicle',
    description:
      'A string describing the associated vehicle, if any, or the ID if a description could not be found.',
    width: 150
  },
  {
    field: 'total',
    headerName: 'Total',
    description: 'The total cost of the estimate.',
    width: 100,
    valueFormatter: (params) => formatCurrency(params.value as number, true)
  },
  {
    field: 'created',
    headerName: 'Created Date',
    description: 'Created Date of the Repair Order',
    valueFormatter: (params) => formatMdyDate(params.value as Date)
  },
  {
    field: 'concern',
    headerName: 'Concern',
    description: 'The concern associated with the estimate.',
    flex: 1
  },
  {
    field: 'isEstimate',
    headerName: 'isEstimate',
    type: 'boolean',
    description: 'Whether the estimate is an estimate or not.',
    width: 100
  },
  {
    field: 'status',
    headerName: 'Status',
    description: 'Status of the Estimate or Repair Order',
    type: 'string'
  }
]

export default function Estimates() {
  const navigate = useNavigate()
  const { currentUser, userShopId } = useContext(AuthContext)

  const accessToken = loggingAssert(
    'No access token found where one was expected',
    currentUser?.accessToken
  )
  const shopId = loggingAssert(
    'No shop ID found where one was expected',
    userShopId
  )

  const { data: repairOrderList, isFetching: repairOrderListIsFetching } =
    useGetRepairOrdersQuery(
      {
        authorization: accessToken,
        shop: shopId,
        isEstimate: true
      },
      { refetchOnMountOrArgChange: true }
    )
  const repairOrderListCustomers = useMemo(
    () =>
      Array.from(
        new Set(
          (repairOrderList || []).map((entity) => entity.entry!.info!.customer!)
        )
      ),
    [repairOrderList]
  )
  const repairOrderListVehicles = useMemo(
    () =>
      Array.from(
        new Set(
          (repairOrderList || [])
            .filter((entity) => !!entity.entry!.info?.vehicle)
            .map((entity) => entity.entry!.info!.vehicle!)
        )
      ),
    [repairOrderList]
  )
  const repairOrderListAppointments = useMemo(
    () =>
      Array.from(
        new Set(
          (repairOrderList || [])
            .filter((entity) => !!entity.entry!.info?.appointment)
            .map((entity) => entity.entry!.info!.appointment!)
        )
      ),
    [repairOrderList]
  )

  const { customerLookup } = useCustomerLookup(
    repairOrderListCustomers,
    repairOrderListIsFetching
  )
  const { vehicleLookup } = useVehicleLookup(
    repairOrderListVehicles,
    repairOrderListIsFetching
  )
  const { appointmentLookup } = useAppointmentLookup(
    repairOrderListAppointments
  )

  const rows = useMemo(() => {
    if (
      !repairOrderList ||
      !customerLookup ||
      !vehicleLookup ||
      !appointmentLookup
    ) {
      return []
    }
    return (repairOrderList || []).map((repairOrder) => {
      const customerId = repairOrder.entry!.info!.customer!
      const vehicleId = repairOrder.entry!.info!.vehicle
      const appointmentId = repairOrder.entry!.info!.appointment
      const isEstimate = repairOrder.entry!.info!.isEstimate

      let customerName = ''
      if (customerId in customerLookup) {
        customerName = formatName(customerLookup[customerId])
      }

      return {
        id: repairOrder.entry!.id,
        isEstimate,
        localId: repairOrder.entry!.localId,
        created: repairOrder.entry!.created!,
        customer: customerName,
        vehicle:
          vehicleId && vehicleId in vehicleLookup
            ? describeVehicle(vehicleLookup[vehicleId])
            : '',
        total: repairOrder!.totalPricing!.total,
        concern: repairOrder.entry!.info!.concern || '',
        status: isEstimate ? 'Estimate' : repairOrder.status!.displayName,
        appointment:
          appointmentId && appointmentId in appointmentLookup
            ? localDateTimeArrayToDate(
                appointmentLookup[appointmentId].startDateTime!
              ).toLocaleString()
            : ''
      }
    })
  }, [repairOrderList, customerLookup, vehicleLookup, appointmentLookup])

  const { gridBoxHeight } = useContext(AppContext)

  return (
    <Page title="Estimates">
      <OutlinedBox
        title="Estimates"
        noPadding={true}
        actionsRight={
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate(AppRoutes.NEW_ESTIMATE)}>
            Estimate
          </Button>
        }>
        <Box sx={{ height: gridBoxHeight }}>
          <DataGridPro
            loading={
              !repairOrderList ||
              !customerLookup ||
              !vehicleLookup ||
              !appointmentLookup
            }
            columns={columns}
            rows={rows}
            density="compact"
            initialState={{
              filter: {
                filterModel: {
                  items: [
                    {
                      field: 'isEstimate',
                      operator: 'is',
                      value: 'true'
                    }
                  ]
                }
              },
              columns: {
                columnVisibilityModel: {
                  isEstimate: false,
                  status: false
                }
              }
            }}
            sx={{ border: '0px' }}
          />
        </Box>
      </OutlinedBox>
    </Page>
  )
}
